import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import DashLayout, { scrollToId } from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KPI, Panel, EmptyRow } from "@/components/DashboardWidgets";
import {
  Wallet, Award, ClipboardCheck, Calendar, BookOpen, ClipboardList, Library, Bus, FileText, Bell, CalendarClock, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

export default function ParentPortal() {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [homeworkList, setHomeworkList] = useState([]);
  const [notices, setNotices] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [payingId, setPayingId] = useState("");
  const [err, setErr] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [tab, setTab] = useState("results");
  const today = new Date().toISOString().slice(0, 10);
  const [lv, setLv] = useState({ from_date: today, to_date: today, reason: "" });

  const loadHw = () => { api.get("/erp/homework").then((r) => setHomeworkList(r.data)).catch(() => {}); };

  useEffect(() => {
    api.get("/stats/student").then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "Error"));
    api.get("/fee/invoices?limit=100").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/erp/notices").then((r) => setNotices(r.data)).catch(() => {});
    api.get("/erp/holidays?upcoming=true").then((r) => setHolidays(r.data)).catch(() => {});
    loadHw();
  }, []);

  const submitHw = async (id) => {
    try {
      await api.post(`/erp/homework/${id}/submit`, { note: "" });
      toast.success("Marked submitted");
      loadHw();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payOnline = async (inv) => {
    setPayingId(inv.id);
    try {
      const { data } = await api.post("/fee/checkout", { invoice_id: inv.id, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setPayingId("");
    }
  };

  if (err) return <DashLayout variant="parent" title="Parent Portal"><div className="text-slate-500">{err}</div></DashLayout>;
  if (!data?.student) return <DashLayout variant="parent" title="Parent Portal"><div className="text-slate-500">Loading…</div></DashLayout>;

  const s = data.student;
  const childId = s.id || "child";
  const att = data.attendance || { total: 0, present: 0, percent: 0 };
  const totalPaid = (data.deposits || []).reduce((a, d) => a + (d.amount || 0), 0);

  const goTab = (t) => () => { setTab(t); scrollToId("pp-tabs"); };
  const linkActions = {
    "Fee Status": goTab("fees"),
    "Exam Schedule": goTab("exams"),
    "Results": goTab("results"),
    "Notices": goTab("notices"),
    "Documents": goTab("info"),
    "Homework": goTab("homework"),
    "Apply Leave": () => setLeaveOpen(true),
  };

  const homeworkStats = (() => {
    const total = homeworkList.length;
    const done = homeworkList.filter((h) => h.submitted).length;
    return { total, done, pending: total - done };
  })();

  const applyLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leaves", { ...lv, applicant_type: "parent" });
      toast.success("Leave request submitted");
      setLeaveOpen(false);
      setLv({ from_date: today, to_date: today, reason: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashLayout variant="parent" title="Parent Portal" linkActions={linkActions}>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-20 h-24 bg-slate-100 border-2 border-slate-300 rounded overflow-hidden">
          {s.photo_url ? <img src={s.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="text-xs text-slate-400 flex items-center justify-center h-full">Photo</div>}
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Parent Portal</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="parent-student-name">{s.first_name} {s.last_name}</h1>
          <div className="text-sm text-slate-500 mt-1">Class {s.class_name} {s.section || ""} · Reg: {s.registration_no || "—"}</div>
          <div className="mt-3 max-w-xs">
            <Label className="mb-1 block text-xs text-slate-500">Child / Children</Label>
            <Select value={childId} onValueChange={() => {}}>
              <SelectTrigger data-testid="pp-child-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={childId}>{s.first_name} {s.last_name} — Class {s.class_name}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button id="pp-leave" onClick={() => setLeaveOpen(true)} data-testid="pp-apply-leave">
          <CalendarClock className="w-4 h-4 mr-1" />Apply Leave
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div id="pp-attendance" className="scroll-mt-6"><KPI icon={ClipboardCheck} label="Today Attendance" value={`${att.percent}%`} sub={`${att.present}/${att.total}`} color="text-emerald-700 bg-emerald-50" testId="pp-att" /></div>
        <KPI icon={Wallet} label="Total Fees Paid" value={`₹${totalPaid.toLocaleString()}`} color="text-emerald-700 bg-emerald-50" testId="pp-fees" />
        <KPI icon={BookOpen} label="Upcoming Exams" value={(data.upcoming_exams || []).length} color="text-indigo-700 bg-indigo-50" testId="pp-exams" />
        <KPI icon={Award} label="Results" value={(data.results || []).length} color="text-purple-700 bg-purple-50" testId="pp-results" />
        <div id="pp-homework" className="scroll-mt-6"><KPI icon={ClipboardList} label="Homework" value={homeworkStats.done + "/" + homeworkStats.total} sub={homeworkStats.pending + " pending"} color="text-amber-700 bg-amber-50" testId="pp-hw" /></div>
        <KPI icon={Library} label="Library" value="—" sub="soon" color="text-slate-700 bg-slate-100" testId="pp-lib" />
        <div id="pp-bus" className="scroll-mt-6"><KPI icon={Bus} label="Bus Tracking" value="—" sub="soon" color="text-yellow-700 bg-yellow-50" testId="pp-bus" /></div>
        <KPI icon={FileText} label="Documents" value="—" sub="soon" color="text-rose-700 bg-rose-50" testId="pp-docs" />
        <KPI icon={Bell} label="Notices" value={(notices || []).length} color="text-pink-700 bg-pink-50" testId="pp-notices" />
        <KPI icon={Calendar} label="Session" value={s.session || "2026-27"} color="text-blue-700 bg-blue-50" testId="pp-session" />
      </div>

      <div id="pp-tabs" className="scroll-mt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="results" data-testid="tab-parent-results">Results</TabsTrigger>
            <TabsTrigger value="fees" data-testid="tab-parent-fees">Fees</TabsTrigger>
            <TabsTrigger value="exams" data-testid="tab-parent-exams">Exam Schedule</TabsTrigger>
            <TabsTrigger value="notices" data-testid="tab-parent-notices">Notices</TabsTrigger>
            <TabsTrigger value="homework" data-testid="tab-parent-homework">Homework</TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-parent-info">Info</TabsTrigger>
          </TabsList>

          <TabsContent value="results">
            <Card className="p-0 overflow-hidden">
              <Table><TableHeader><TableRow><TableHead>Exam</TableHead><TableHead>Subjects</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>{(data.results || []).length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">No results yet</TableCell></TableRow>}
                {(data.results || []).map(r => { const total = Object.values(r.subject_marks || {}).reduce((a, b) => a + (Number(b) || 0), 0); return (
                  <TableRow key={r.id}><TableCell className="font-mono text-xs">{(r.exam_id || "").slice(-6)}</TableCell>
                    <TableCell className="text-sm">{Object.entries(r.subject_marks || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</TableCell>
                    <TableCell className="text-right font-bold tabular-nums">{total}</TableCell></TableRow>);})}</TableBody></Table>
            </Card>
          </TabsContent>

          <TabsContent value="fees">
            <Card className="p-4 mb-3 bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200">
              <h3 className="font-bold text-sm mb-2">Outstanding Invoices</h3>
              {invoices.filter(i => i.due > 0).length === 0 ? (
                <div className="text-xs text-slate-500">No outstanding fees — all clear!</div>
              ) : (
                <div className="space-y-2">
                  {invoices.filter(i => i.due > 0).map(i => (
                    <div key={i.id} className="flex items-center justify-between bg-white rounded p-3 border" data-testid={`pp-inv-${i.id}`}>
                      <div>
                        <div className="font-semibold text-sm">{i.period} · Class {i.class_name}</div>
                        <div className="text-xs text-slate-500">Due: {i.due_date} · Total ₹{i.total.toLocaleString()} · Paid ₹{i.paid.toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-red-700 tabular-nums">₹{i.due.toLocaleString()}</span>
                        <Button size="sm" onClick={() => payOnline(i)} disabled={payingId === i.id} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`pp-pay-${i.id}`}>
                          <CreditCard className="w-4 h-4 mr-1" />{payingId === i.id ? "Redirecting…" : "Pay Now"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-0 overflow-hidden">
              <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>{(data.deposits || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">No fee records</TableCell></TableRow>}
                {(data.deposits || []).map(d => <TableRow key={d.id}><TableCell>{d.date}</TableCell><TableCell>{d.fee_type}</TableCell><TableCell><Badge variant="outline">{d.mode}</Badge></TableCell><TableCell className="text-right tabular-nums font-semibold">₹{(d.amount || 0).toLocaleString()}</TableCell></TableRow>)}</TableBody></Table>
            </Card>
          </TabsContent>

          <TabsContent value="exams">
            <Panel title="Exam Schedule" testId="pp-exam-list">
              {(data.upcoming_exams || []).length === 0 ? <EmptyRow text="No upcoming exams" /> : (
                <ul className="space-y-2">
                  {data.upcoming_exams.map((e) => (
                    <li key={e.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                      <div><div className="font-semibold">{e.name}</div><div className="text-xs text-slate-500">{e.subject} · {e.max_marks} marks</div></div>
                      <div className="text-xs text-slate-500 font-mono">{e.exam_date}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </TabsContent>

          <TabsContent value="notices">
            <Panel title="Notices & Events" testId="pp-notice-list">
              {(notices || []).length === 0 ? <EmptyRow text="No notices" /> : (
                <ul className="space-y-2">
                  {notices.map((n) => (
                    <li key={n.id} className="text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`notice-${n.id}`}>
                      <div className="font-semibold">{n.title}</div>
                      <div className="text-xs text-slate-600">{n.body}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 10)} · {n.audience}{n.class_name ? ` · Class ${n.class_name}` : ""}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            <Panel title="Upcoming Holidays" testId="pp-holiday-list">
              {holidays.length === 0 ? <EmptyRow text="No upcoming holidays" /> : (
                <ul className="space-y-2">
                  {holidays.map((h) => (
                    <li key={h.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`pp-holiday-${h.id}`}>
                      <div className="font-semibold">{h.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{h.date} · <Badge variant="outline">{h.scope}</Badge></div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </TabsContent>

          <TabsContent value="homework">
            <Panel title={`Homework — ${homeworkStats.done}/${homeworkStats.total} completed · ${homeworkStats.pending} pending`} testId="pp-hw-list">
              {homeworkList.length === 0 ? <EmptyRow text="No homework assigned" /> : (
                <ul className="space-y-2">
                  {homeworkList.map((h) => {
                    const overdue = !h.submitted && new Date(h.due_date) < new Date();
                    return (
                      <li key={h.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0 gap-2" data-testid={`pp-hw-${h.id}`}>
                        <div className="flex-1">
                          <div className="font-semibold">{h.title}</div>
                          <div className="text-xs text-slate-500">{h.description || "—"}</div>
                          <div className="text-xs text-slate-400 mt-0.5">Due: {h.due_date}{h.submitted && ` · Submitted ${(h.submitted_at || "").slice(0, 10)}`}</div>
                        </div>
                        {h.submitted ? (
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">✓ Submitted</Badge>
                        ) : (
                          <Button size="sm" onClick={() => submitHw(h.id)} className={overdue ? "bg-red-600 hover:bg-red-700" : "bg-blue-700 hover:bg-blue-800"} data-testid={`pp-hw-submit-${h.id}`}>
                            {overdue ? "Submit (Overdue)" : "Mark Submitted"}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          </TabsContent>

          <TabsContent value="info">
            <Card className="p-6 grid md:grid-cols-2 gap-4 text-sm">
              <div><b className="text-slate-500">DOB:</b> {s.dob}</div>
              <div><b className="text-slate-500">Gender:</b> {s.gender}</div>
              <div><b className="text-slate-500">Blood Group:</b> {s.blood_group || "—"}</div>
              <div><b className="text-slate-500">Board:</b> {s.board}</div>
              <div><b className="text-slate-500">Father:</b> {s.parent_details?.father?.name || "—"}</div>
              <div><b className="text-slate-500">Mother:</b> {s.parent_details?.mother?.name || "—"}</div>
              <div className="md:col-span-2"><b className="text-slate-500">Address:</b> {s.address_details?.permanent || "—"}</div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
          <form onSubmit={applyLeave} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">From</Label><Input type="date" required value={lv.from_date} onChange={(e) => setLv({ ...lv, from_date: e.target.value })} data-testid="pp-lv-from" /></div>
              <div><Label className="mb-1 block text-xs">To</Label><Input type="date" required value={lv.to_date} onChange={(e) => setLv({ ...lv, to_date: e.target.value })} data-testid="pp-lv-to" /></div>
            </div>
            <div><Label className="mb-1 block text-xs">Reason</Label><Textarea rows={3} required value={lv.reason} onChange={(e) => setLv({ ...lv, reason: e.target.value })} data-testid="pp-lv-reason" /></div>
            <DialogFooter>
              <Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="pp-lv-submit">Submit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
